import { Firestore, collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import { processReference } from "./locationVersion/referenceUtils";

/**
 * Helper function to get camp locations for an organization
 */
export const getCampLocationsFromOrg = async (
  db: Firestore,
  orgId: string,
  options?: { locationIds?: string[] }
) => {
  try {
    console.log("Getting locations for organization:", orgId);

    const locationIds = options?.locationIds?.filter((x) => typeof x === "string") || [];
    if (locationIds.length > 0) {
      const uniqueIds = Array.from(new Set(locationIds));
      const snaps = await Promise.all(uniqueIds.map((id) => getDoc(doc(db, "campLocations", id))));
      const locations: CampLocation[] = [];
      for (const snap of snaps) {
        if (!snap.exists()) continue;
        const locationData = { id: snap.id, ...(snap.data() as any) } as Record<string, any>;
        if (locationData.providedByOrg) {
          locationData.providedByOrg = processReference(locationData.providedByOrg);
        }
        const castedLocation = {
          ...locationData,
          name: locationData.name || "",
          visibility: locationData.visibility || "v_draft",
          address: locationData.address || { country: "USA" },
          doesWaitlist: locationData.doesWaitlist ?? null,
          doesScholarships: locationData.doesScholarships ?? null,
        } as CampLocation;
        locations.push(castedLocation);
      }
      return locations;
    }
    
    // Create a document reference to use in the query
    const orgRef = doc(db, "campOrganizations", orgId);
    
    // Try first approach: query using providedByOrg as DocumentReference
    console.log(`Approach 1: Querying where providedByOrg == orgRef ${orgRef.path}`);
    const locationsRef = collection(db, "campLocations");
    let q = query(locationsRef, where("providedByOrg", "==", orgRef));
    
    let querySnapshot = await getDocs(q);
    
    // If first approach returns no documents, try the second approach
    if (querySnapshot.empty) {
      console.log("No locations found with direct reference match, trying with organization map...");
      
      // Try querying with the new field structure - check for organization.id field
      q = query(locationsRef, where("providedByOrg.id", "==", orgId));
      console.log(`Trying with providedByOrg.id == ${orgId}`);
      querySnapshot = await getDocs(q);
      
      // If still no results, try the old approach as fallback
      if (querySnapshot.empty) {
        console.log("No locations found with organization.id field, trying with legacy organizationId field");
        
        // Try third approach: Query where providedByOrg directly matches orgId (string)
        q = query(locationsRef, where("providedByOrg", "==", orgId));
        console.log(`Approach 3: Querying where providedByOrg == ${orgId} (string value)`);
        querySnapshot = await getDocs(q);
      }
    }
    
    // If all queries return no documents, log this information
    if (querySnapshot.empty) {
      console.log(`No locations found for organization ID: ${orgId} after trying all approaches`);
      return [];
    }
    
    console.log(`Found ${querySnapshot.size} locations for organization ${orgId}`);
    const locations: CampLocation[] = [];
    
    querySnapshot.forEach((doc) => {
      try {
        console.log(`Processing location document: ${doc.id}`);
        const locationData = {
          id: doc.id,
          ...doc.data()
        } as Record<string, any>;
        
        // Log the type of providedByOrg to help debug
        console.log(`Document ${doc.id}: providedByOrg type:`, 
          locationData.providedByOrg ? typeof locationData.providedByOrg : 'undefined', 
          "Value:", locationData.providedByOrg);
        
        // Process document references to ensure consistent format
        if (locationData.providedByOrg) {
          locationData.providedByOrg = processReference(locationData.providedByOrg);
        }
        
        // Type casting with fallback values
        const castedLocation = {
          ...locationData,
          name: locationData.name || '',
          visibility: locationData.visibility || 'v_draft',
          address: locationData.address || { country: 'USA' },
          doesWaitlist: locationData.doesWaitlist ?? null,
          doesScholarships: locationData.doesScholarships ?? null,
        } as CampLocation;
        
        locations.push(castedLocation);
        console.log(`Added location to result: ${castedLocation.name} (${castedLocation.id})`);
      } catch (error) {
        console.error(`Error processing location document ${doc.id}:`, error);
        // Continue processing other documents even if one fails
      }
    });
    
    console.log(`Returning ${locations.length} locations for organization ID: ${orgId}`);
    return locations;
  } catch (error) {
    console.error("Error getting camp locations:", error);
    // Return empty array on error to avoid undefined returns
    return [];
  }
};
